import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { fmt } from "@/lib/api";

const Section = ({ title, rows, tone, total, testid }) => (
  <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
    <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
      <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500">{title}</span>
      <span className={`font-mono font-semibold ${tone}`} data-testid={`${testid}-total`}>
        {fmt(total)}
      </span>
    </div>
    <div className="divide-y divide-gray-100">
      {rows.length === 0 && <div className="px-5 py-4 text-sm text-gray-400">No entries</div>}
      {rows.map((r) => (
        <div key={r.category} className="px-5 py-2.5 flex items-center justify-between">
          <span className="text-sm text-gray-700">{r.category}</span>
          <span className={`font-mono text-sm ${tone}`}>{fmt(r.amount)}</span>
        </div>
      ))}
    </div>
  </div>
);

export const PnlView = ({ pnl, company, periodText, generatedAt }) => {
  const chartData = [
    { name: "Income", value: pnl.total_income, fill: "#059669" },
    { name: "Expenses", value: pnl.total_expense, fill: "#DC2626" },
    { name: "Net Profit", value: pnl.net_profit, fill: "#0A0A0A" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="pnl-view"
    >
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-testid="pnl-report-header">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-gray-900">
            {company || "Your Company"}
          </h2>
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500">
            Generated {generatedAt}
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Profit &amp; Loss Statement · <span className="font-semibold text-gray-700">{periodText}</span>
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <h3 className="font-heading text-xl font-bold text-gray-900 mb-4">Income vs Expenses</h3>
        <div className="h-64" style={{ minHeight: 256 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => fmt(v)} cursor={{ fill: "#F3F4F6" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {chartData.map((e, i) => (
                  <Cell key={i} fill={e.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section
          title="Income"
          rows={pnl.income}
          tone="text-emerald-600"
          total={pnl.total_income}
          testid="pnl-income"
        />
        <Section
          title="Expenses"
          rows={pnl.expense}
          tone="text-rose-600"
          total={pnl.total_expense}
          testid="pnl-expense"
        />
      </div>

      <div
        className="bg-black text-white rounded-lg p-6 flex items-center justify-between"
        data-testid="pnl-net-profit"
      >
        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-300">
          Net Profit / Loss
        </span>
        <span className="font-mono text-2xl font-bold">{fmt(pnl.net_profit)}</span>
      </div>
    </motion.div>
  );
};
